import React, { useContext, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { usePage } from "../../routes";

import { useForm } from "react-hook-form";
import { useAddPanelsToSwitchgear } from "../data/Assets/useAddPanelToHvSwitchgear";
import { usePanelDelete } from "../data/Assets/usePanelDelete";
import { usePanelsBySwitchgear } from "../data/Assets/usePanelsBySwitchgear";

import { Alert, Box, Divider, Grid, Skeleton, Stack } from "@mui/material";
import { AuthContext } from "../contexts/AuthContext";

import { AddButton } from "../shared/FormLayout/IconButtons/AddButton";
import { CancelButton } from "../shared/FormLayout/IconButtons/CancelButton";
import { ConfirmButton } from "../shared/FormLayout/IconButtons/ConfirmButton";
import { EditButton } from "../shared/FormLayout/IconButtons/EditButton";
import {
  FloatingLabelInput,
  FloatingLabelInputWithUpdatableStyle,
} from "../shared/FormLayout/InputTemplates/FloatingLabelInput";

import DeleteConfirmationButton from "../shared/FormLayout/Buttons/DeleteConfirmationButton";
import LabelText from "../shared/LabelValueFormatters/LabelText";
import ValueText from "../shared/LabelValueFormatters/ValueText";
import { MessageCard, MessageType } from "../shared/MessageCard";
import { Snackbar } from "../shared/Snackbar";
import { ValidationMessage } from "../shared/ValidationMessage";

import { HVSubstationSetupErrorCode } from "../shared/enums/ErrorCodes";
import { Asset } from "../shared/interfaces/Asset";
import { HvSubstationErrorResponse } from "../shared/interfaces/HvSubstation";
import { getNextPanelNo } from "./PanelNumberIncrement";
import { orderPanelsByPanelNumberNumerically } from "./PanelNumberOrdering";
import { validPanelNumber } from "./PanelNumberRegex";

import { AccessControlContext } from "../contexts/AccessControlContext";
import { usePanelUpdate } from "../data/Assets/usePanelUpdate";
import {
  ExistingSurveyableAsset,
  getSubAssetType,
  Panel,
  PanelsSubmission,
} from "../shared/interfaces/SurveyableAsset";
import { WorkflowViewer } from "../workflows/WorkflowViewer";

interface Props {
  editable?: boolean;
  switchgearAsset?: Asset | ExistingSurveyableAsset;
  editingStyle?: string;
  customerId?: number;
}

export function UpdatablePanelDetails(props: Props) {
  const assetType =
    (props.switchgearAsset as Asset)?.assetTypeKey ?? (props.switchgearAsset as ExistingSurveyableAsset)?.assetType;

  const subAssetLabel = getSubAssetType(assetType);

  const { t } = useTranslation();

  const workflowViewerPath = usePage(WorkflowViewer.name)?.path;

  const { editable, switchgearAsset } = props;
  const authContext = useContext(AuthContext);
  const accessControlContext = useContext(AccessControlContext);
  const userCanDeletePanels = accessControlContext.canI("delete", "panels", props.customerId);

  const {
    register,
    formState: { errors },
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
  } = useForm();

  const { refetch: refetchSwitchgearPanels, ...retrievedSwitchgearPanels } = usePanelsBySwitchgear(
    switchgearAsset?.id,
    {
      onSuccess: handleRetrievedSwitchgearPanelsSuccess,
      onError: onFetchPanelsError,
    },
  );

  const [updatablePanels, setUpdatablePanels] = useState<Panel[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [selectedPanel, setSelectedPanel] = useState<Panel | undefined>(undefined);
  const [panelNumberDuplicated, setPanelNumberDuplicated] = useState<boolean>(false);
  const [creatingNewPanel, setCreatingNewPanel] = useState<boolean>(false);

  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorSnackbarText, setErrorSnackbarText] = useState<string>("");

  const [deletionPanelNumber, setDeletionPanelNumber] = useState<string | undefined>();
  const [affectedWorkflowIds, setAffectedWorkflowIds] = useState<number[] | undefined>(undefined);

  const {
    mutate: addPanelsToSwitchgear,
    isLoading: addingPanelsLoading,
    reset: resetAddPanelsToSwitchgear,
  } = useAddPanelsToSwitchgear({
    onSuccess: handleAddPanelsToSwitchgearSuccess,
    onError: onUpdatePanelError,
  });

  const panelUpdate = usePanelUpdate({
    onSuccess: handlePanelUpdateSuccess,
    onError: onUpdatePanelError,
  });

  const panelDelete = usePanelDelete({
    onSuccess: onDeletePanelSuccess,
    onError: onDeletePanelError,
  });

  function handleInputChange(event: any, index: number) {
    const name = event.target.name;
    const value = event.target.value;

    if (errors) clearErrors();

    if (updatablePanels.length > 0) {
      let updatedSwitchgearPanels = [...updatablePanels];
      updatedSwitchgearPanels[index] = {
        ...updatedSwitchgearPanels[index],
        [name]: value,
      };
      setUpdatablePanels(updatedSwitchgearPanels);
    }
  }

  useEffect(() => {
    if (authContext.userIsAuthenticated) {
      refetchSwitchgearPanels();
    }
  }, [authContext.userIsAuthenticated, refetchSwitchgearPanels]);

  useEffect(() => {
    setLoadingData(retrievedSwitchgearPanels.isLoading || addingPanelsLoading || panelUpdate.isLoading);
  }, [addingPanelsLoading, panelUpdate.isLoading, retrievedSwitchgearPanels.isLoading]);

  function handleDeletePanelClick(panel: Panel) {
    setDeletionPanelNumber(panel.panelNo);
    userCanDeletePanels && panel.panelAssetId && panelDelete.mutate(panel.panelAssetId);
  }

  function onDeletePanelSuccess() {
    refetchSwitchgearPanels();
  }

  function onDeletePanelError(errorResponse: Response) {
    if (errorResponse.status === 403) {
      setErrorSnackbarText(
        t("common.operationForbiddenError", {
          operation: t("common.delete"),
          item: t(subAssetLabel),
        }),
      );
      setShowErrorSnackbar(true);
    } else if (errorResponse.status === 409) {
      errorResponse.json().then((error: HvSubstationErrorResponse) => {
        if (error.errorCode === HVSubstationSetupErrorCode.PanelIdExistsOnActiveWorkflowException) {
          setErrorSnackbarText(
            t("panels.unableToDeletePanelError", {
              val: deletionPanelNumber,
              asset: subAssetLabel,
            }),
          );
          setAffectedWorkflowIds(error.affectedIds);
        } else if (error.errorCode === HVSubstationSetupErrorCode.AssetIdDoesNotBelongToAPanelException) {
          setErrorSnackbarText(t("panels.panelNotFoundError", { val: subAssetLabel }));
          setShowErrorSnackbar(true);
        }
      });
    } else {
      setErrorSnackbarText(t("panels.panelDeleteGenericError", { val: subAssetLabel }));
      setShowErrorSnackbar(true);
    }
  }

  function onFetchPanelsError() {
    setErrorSnackbarText(t("common.dataFetchError", { val: subAssetLabel }));
    setShowErrorSnackbar(true);
  }

  function onUpdatePanelError() {
    setErrorSnackbarText(t("common.dataUpdateError", { val: subAssetLabel }));
    setShowErrorSnackbar(true);
  }

  function handleRetrievedSwitchgearPanelsSuccess(data: Panel[]) {
    if (!creatingNewPanel && !selectedPanel) {
      const sortedPanels = orderPanelsByPanelNumberNumerically(data, "asc");
      setUpdatablePanels(sortedPanels);
    }
  }

  function handleAddPanelsToSwitchgearSuccess() {
    resetAddPanelsToSwitchgear();
    refetchSwitchgearPanels();
  }

  useEffect(() => {
    // Check for panel number duplication
    let panelNos = updatablePanels.map((panel) => panel.panelNo);
    setPanelNumberDuplicated(
      panelNos && panelNos.some((panelNo) => panelNos && panelNos.indexOf(panelNo) !== panelNos.lastIndexOf(panelNo)),
    );
  }, [updatablePanels]);

  function handleSavePanelChangesButtonClick(index: number, panelId?: number) {
    setValue("index", index);
    setValue("panelId", panelId);
    submit();
  }

  const submit = handleSubmit(() => {
    const index = getValues("index");
    const panelId = getValues("panelId");

    if (panelId) {
      const editedPanel = updatablePanels.find(
        (panel) => selectedPanel && panel.panelAssetId === selectedPanel.panelAssetId,
      );
      editedPanel && panelUpdate.mutate(editedPanel);
    } else {
      // The Panel ID does not exist, so the panel is new and needs to be created
      const panel = updatablePanels[index];
      switchgearAsset && panel && handleAddPanel(switchgearAsset.id, panel);
    }
    setSelectedPanel(undefined);
    setValue("panelNo", "");
  });

  function handlePanelUpdateSuccess() {
    refetchSwitchgearPanels();
  }

  useEffect(() => {
    if (creatingNewPanel && updatablePanels.length > 0) {
      !updatablePanels[updatablePanels.length - 1].panelAssetId &&
        setSelectedPanel(updatablePanels[updatablePanels.length - 1]);
    }
  }, [creatingNewPanel, updatablePanels]);

  function handleAddPanel(switchgearId: number, panel: Panel) {
    if (editable && !panelNumberDuplicated && panel.panelNo !== "") {
      const panelsSubmission: PanelsSubmission = {
        surveyableAssetId: switchgearId,
        panels: [panel],
      };
      addPanelsToSwitchgear(panelsSubmission);
      setCreatingNewPanel(false);
    }
  }

  function handleEditButtonClick(panel: Panel) {
    setValue("panelNo", panel.panelNo);
    setSelectedPanel(panel);
  }

  function handleCancelButtonClicked(_index: number) {
    setSelectedPanel(undefined);
    setCreatingNewPanel(false);
    setUpdatablePanels(retrievedSwitchgearPanels.data || []);
    setValue("panelNo", "");
    clearErrors();
  }

  function handleAddButtonClick() {
    let prepopulatedPanelNo = "1";

    if (updatablePanels.length > 0) {
      const sortedPanels = orderPanelsByPanelNumberNumerically(updatablePanels, "asc");
      const lastPanelNo = sortedPanels.reverse().find((panel) => panel.panelNo !== "")?.panelNo || "";
      prepopulatedPanelNo = getNextPanelNo(lastPanelNo);
    }
    setUpdatablePanels((prevState) => [...prevState, { name: "", panelNo: prepopulatedPanelNo, assetTag: "" }]);
    setCreatingNewPanel(true);
    setValue("panelNo", prepopulatedPanelNo);
  }

  function panelNumberIsDuplicated(newPanelNo: string) {
    let existingPanelNos = updatablePanels.map((panel) => panel.panelNo);
    return existingPanelNos && existingPanelNos.filter((panelNo) => panelNo === newPanelNo).length > 1;
  }

  const handleErrorSnackbarClose = () => {
    setShowErrorSnackbar(false);
  };

  const handlePanelDeletionSuccessSnackbarClose = () => {
    panelDelete.reset();
  };

  if (loadingData) {
    return (
      <Box>
        {[...Array(10).keys()].map((i) => (
          <Skeleton key={`skeleton-row-${i}`} variant="rectangular" sx={{ my: 2 }} />
        ))}
      </Box>
    );
  } else
    return (
      <>
        {panelDelete.isError && affectedWorkflowIds && (
          <MessageCard
            title={t("panels.unableToDeletePanelError", {
              val: deletionPanelNumber,
              asset: subAssetLabel,
            })}
            content={t("panels.panelInUseError", {
              val: t("workflows.activeWorkflow", {
                count: affectedWorkflowIds.length,
              }),
              asset: subAssetLabel,
            })}
            secondaryText={t("panels.panelRemoveReferenceError", {
              val: t("workflows.workflow", {
                count: affectedWorkflowIds.length,
              }),
              asset: subAssetLabel,
            })}
            cardActions={
              <Box display="flex" flexDirection="column">
                {affectedWorkflowIds.map((workflowId) => (
                  <Link
                    key={`link-${workflowId}`}
                    to={workflowViewerPath?.replace(":workflowId", workflowId.toString() || "") || ""}
                  >
                    {workflowId}
                  </Link>
                ))}
              </Box>
            }
            messageType={MessageType.Error}
          />
        )}
        {updatablePanels.length > 0 ? (
          <Stack spacing={0} divider={<Divider />}>
            <Grid container flex={1} mb={1}>
              <Grid item xs={2.5} display="flex" justifyContent="center" alignItems="center">
                <LabelText>{t("panels.panelNumber", { val: t(subAssetLabel) })}</LabelText>
              </Grid>
              <Grid item xs={4.5} display="flex" justifyContent="flex-start" alignItems="center">
                <LabelText>{t("panels.panelName", { val: t(subAssetLabel) })}</LabelText>
              </Grid>
              <Grid item xs={3} display="flex" justifyContent="flex-start" alignItems="center">
                <LabelText>{t("panels.assetTag")}</LabelText>
              </Grid>
            </Grid>

            {updatablePanels.map((panel: Panel, index: number) => {
              const isNewPanel = selectedPanel?.panelAssetId == null && panel.panelAssetId == null;
              const isEditMode =
                editable && selectedPanel && (isNewPanel || selectedPanel?.panelAssetId === panel.panelAssetId);

              if (isEditMode)
                return (
                  <React.Fragment key={`panelFragment-${panel.panelAssetId || "panelEdit"}`}>
                    <Grid container spacing={1} my={1}>
                      <Grid item xs={12} lg={2.5}>
                        <Stack>
                          <FloatingLabelInputWithUpdatableStyle
                            value={panel.panelNo}
                            label={t("panels.panelNumber", {
                              val: t(subAssetLabel),
                            })}
                            maxLength={5}
                            inputClassName={errors?.panelNo ? "p-invalid" : undefined}
                            {...register("panelNo", {
                              onChange: (event) => handleInputChange(event, index),
                              value: panel.panelNo,
                              validate: {
                                notEmpty: (value) => value && value !== "",
                                notDuplicate: (value) => !panelNumberIsDuplicated(value),
                                correctPattern: (value) => validPanelNumber(value),
                              },
                            })}
                          />
                          {errors?.panelNo && errors.panelNo.type === "notDuplicate" && (
                            <ValidationMessage
                              invalidMessage={t("panels.panelDuplication", {
                                val: t(subAssetLabel),
                              })}
                              validationType="error"
                              showIcon
                            />
                          )}
                          {errors?.panelNo && errors.panelNo.type === "notEmpty" && (
                            <ValidationMessage
                              invalidMessage={t("panels.panelNumberRequired", { val: subAssetLabel })}
                              validationType="error"
                              showIcon
                            />
                          )}
                          {errors?.panelNo && errors.panelNo.type === "correctPattern" && (
                            <ValidationMessage
                              invalidMessage={t("panels.incorrectPanelNumberFormat", { val: subAssetLabel })}
                              validationType="error"
                              showIcon
                            />
                          )}
                        </Stack>
                      </Grid>

                      <Grid item xs={12} lg={4.5}>
                        <FloatingLabelInput
                          name="name"
                          value={panel.name || ""}
                          label={t("panels.panelName", { val: t(subAssetLabel) })}
                          onChange={(event: any) => handleInputChange(event, index)}
                          maxLength={50}
                          width="100%"
                        />
                      </Grid>
                      <Grid item xs={12} lg={3}>
                        <FloatingLabelInput
                          name="assetTag"
                          value={panel.assetTag || ""}
                          label={t("panels.assetTag")}
                          onChange={(event) => handleInputChange(event, index)}
                          maxLength={50}
                          width="100%"
                        />
                      </Grid>
                      <Grid item xs={12} lg={2}>
                        <Stack direction="row" justifyContent="flex-end">
                          <ConfirmButton onClick={() => handleSavePanelChangesButtonClick(index, panel.panelAssetId)} />
                          <CancelButton onClick={() => handleCancelButtonClicked(index)} />
                        </Stack>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                );
              else
                return (
                  <React.Fragment key={`fragment-${panel.panelAssetId}`}>
                    <Grid container>
                      <Grid item xs={2.5} display="flex" justifyContent="center" alignItems="center">
                        <LabelText>{panel.panelNo}</LabelText>
                      </Grid>

                      <Grid item xs={4.5} display="flex" justifyContent="flex-start" alignItems="center">
                        <ValueText>{panel?.name}</ValueText>
                      </Grid>
                      <Grid item xs={3} display="flex" justifyContent="flex-start" alignItems="center">
                        <ValueText>{panel?.assetTag}</ValueText>
                      </Grid>
                      <Grid item xs={2} display="flex" justifyContent="flex-end" alignItems="center">
                        <Box display="flex" flexDirection="row">
                          {editable && (
                            <>
                              {userCanDeletePanels && (
                                <DeleteConfirmationButton
                                  confirmationDialogHeader={t("common.deleteConfirmationShort", {
                                    val: `${t(subAssetLabel)} ${panel.panelNo}`,
                                  })}
                                  confirmationDialogMessage={t("common.deleteConfirmation", {
                                    val: `${t(subAssetLabel)} ${panel.panelNo}`,
                                  })}
                                  onConfirmDeleteButtonClick={() => handleDeletePanelClick(panel)}
                                  variant="icon"
                                  disabled={Boolean(selectedPanel)}
                                />
                              )}
                              <EditButton
                                onClick={() => handleEditButtonClick(panel)}
                                disabled={Boolean(selectedPanel)}
                              />
                            </>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                );
            })}
          </Stack>
        ) : (
          <Box display="flex" justifyContent="center" mb={1}>
            <LabelText>{t("panels.panelDetailsEmpty", { val: t(subAssetLabel) })}</LabelText>
          </Box>
        )}

        {editable && !creatingNewPanel && !selectedPanel && (
          <>
            <Divider />
            <Box display="flex" justifyContent="center">
              <AddButton onClick={handleAddButtonClick} />
            </Box>
          </>
        )}

        <Snackbar open={showErrorSnackbar} onClose={handleErrorSnackbarClose}>
          <Alert severity="error" variant="filled">
            {errorSnackbarText}
          </Alert>
        </Snackbar>

        <Snackbar open={panelDelete.isSuccess} onClose={handlePanelDeletionSuccessSnackbarClose}>
          <Alert severity="success" variant="filled">
            {t("panels.panelDeleted", {
              val: deletionPanelNumber,
              asset: subAssetLabel,
            })}
          </Alert>
        </Snackbar>
      </>
    );
}
